// User types
export type UserId = string;

export interface User {
  id: UserId;
  email: string;
}

// Project types
export type ProjectId = string;
export type NodeTypeId = string;
export type ApplicationId = string;

// Price is in micro-euros as a plain number (1 euro = 1,000,000 micro-euros)
export type Price = number;

// TimePrice from backend (price per second in micro-euros)
export interface TimePrice {
  priceInMicroEuroPerSecond: number;
}

export interface Project {
  id: ProjectId;
  owner: UserId;
  name: string;
  applications: ApplicationId[];
}

export enum ProjectValidationErrorType {
  NoHighAvailability = 'NoHighAvailability',
  NoControlNodes = 'NoControlNodes',
  NoWorkerNodes = 'NoWorkerNodes',
  NoName = 'NoName',
}

export interface ProjectValidationError {
  type: ProjectValidationErrorType;
  preventContinue: boolean;
}

export interface NodeType {
  id: NodeTypeId;
  name: string;
  provider: string;
  ram: number;
  cpu: number;
  price: TimePrice;
}

export interface Application {
  id: ApplicationId;
  name: string;
  description: string;
  price: TimePrice;
}

// Deployment types
export enum DeploymentStage {
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  CONFIGURATION = 'CONFIGURATION',
  PROVISIONING = 'PROVISIONING',
  SETUP_CLUSTER = 'SETUP_CLUSTER',
  SETUP_APPLICATIONS = 'SETUP_APPLICATIONS',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

export type NodeId = string;

export enum NodeRole {
  CONTROL_NODE = 'CONTROL_NODE',
  WORKER_NODE = 'WORKER_NODE',
}

export interface Node {
  id: NodeId;
  projectId: ProjectId;
  role: NodeRole;
  type: NodeTypeId;
  createdAt: string;
  deletedAt?: string;
  ip?: string;
  user?: string;
}

export interface DeploymentStatus {
  stage: DeploymentStage;
  kubeconfig?: string;
  sshKey?: string;
}

// API Request/Response types
export interface LoginRequest {
  email: string;
  password: string;
}

export type LoginResponse =
  | { type: 'success'; token: string }
  | { type: 'failure'; error: string };

export interface RefreshTokenResponse {
  token: string;
}

export interface ListProjectsResponse {
  projects: Project[];
}

export interface GetProjectResponse {
  project: Project;
}

export interface SetProjectRequest {
  project: Project;
  controlNodes: Record<NodeTypeId, number>;
  workerNodes: Record<NodeTypeId, number>;
  nodesToDelete: NodeId[];
}

export interface CreateProjectResponse {
  projectId: ProjectId;
}

export interface GetNodeTypesResponse {
  nodeTypes: NodeType[];
}

export interface GetApplicationsResponse {
  applications: Application[];
}

export interface GetStatusResponse {
  status: DeploymentStatus;
  nodes: Node[];
}

export interface NodeUsage {
  first: Node;
  second: string; // ISO 8601 duration format (e.g., "PT9M55.975415920S")
}

export interface CostOverviewProjectEntry {
  project: Project;
  usage: NodeUsage[];
}

export interface CostOverview {
  start: string; // ISO timestamp
  end: string; // ISO timestamp
  usage: CostOverviewProjectEntry[];
  costs: Record<NodeId, Price>;
  userId: UserId;
}

// Utility functions
export const createDefaultProject = (): Project => ({
  id: '00000000-0000-0000-0000-000000000000',
  owner: '00000000-0000-0000-0000-000000000000',
  name: '',
  applications: [],
});

export const createDefaultDeploymentStatus = (): DeploymentStatus => ({
  stage: DeploymentStage.NOT_INITIALIZED,
});

export const validateProject = (
  project: Project,
  controlNodes: Record<NodeTypeId, number>,
  workerNodes: Record<NodeTypeId, number>
): ProjectValidationError[] => {
  const results: ProjectValidationError[] = [];

  const controlNodesCount = Object.values(controlNodes).reduce((sum, count) => sum + count, 0);
  const workerNodesCount = Object.values(workerNodes).reduce((sum, count) => sum + count, 0);

  if (controlNodesCount === 0) {
    results.push({
      type: ProjectValidationErrorType.NoControlNodes,
      preventContinue: true,
    });
  }

  if (workerNodesCount === 0) {
    results.push({
      type: ProjectValidationErrorType.NoWorkerNodes,
      preventContinue: true,
    });
  }

  if (controlNodesCount < 3) {
    results.push({
      type: ProjectValidationErrorType.NoHighAvailability,
      preventContinue: false,
    });
  }

  if (!project.name || project.name.trim() === '') {
    results.push({
      type: ProjectValidationErrorType.NoName,
      preventContinue: true,
    });
  }

  return results;
};

export const formatPrice = (price: Price): string => {
  const euros = price / 1_000_000;
  return `€${euros.toFixed(2)}`;
};

// Format UTC timestamp to local timezone
// Returns format like: "2/12/2026, 3:45:30 PM"
export const formatDateTime = (utcTimestamp: string): string => {
  return new Date(utcTimestamp).toLocaleString();
};

// Format UTC timestamp to local date only
// Returns format like: "2/12/2026"
export const formatDate = (utcTimestamp: string): string => {
  return new Date(utcTimestamp).toLocaleDateString();
};

// Format UTC timestamp to local time only
// Returns format like: "3:45:30 PM"
export const formatTime = (utcTimestamp: string): string => {
  return new Date(utcTimestamp).toLocaleTimeString();
};

// Format UTC timestamp with custom options
export const formatDateTimeCustom = (
  utcTimestamp: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  return new Date(utcTimestamp).toLocaleString(undefined, options);
};

// Convert TimePrice to monthly Price (in micro-euros)
export const timePriceToMonthlyPrice = (timePrice: TimePrice): Price => {
  const secondsPerMonth = 60 * 60 * 24 * 30;
  return timePrice.priceInMicroEuroPerSecond * secondsPerMonth;
};

export const calculateProjectPrice = (
  project: Project,
  controlNodes: Record<NodeTypeId, number>,
  workerNodes: Record<NodeTypeId, number>,
  nodeTypes: NodeType[],
  applications: Application[]
): Price => {
  let totalMicroEuros = 0;

  // Add node costs
  Object.entries(controlNodes).forEach(([nodeTypeId, count]) => {
    const nodeType = nodeTypes.find(nt => nt.id === nodeTypeId);
    if (nodeType) {
      totalMicroEuros += timePriceToMonthlyPrice(nodeType.price) * count;
    }
  });

  Object.entries(workerNodes).forEach(([nodeTypeId, count]) => {
    const nodeType = nodeTypes.find(nt => nt.id === nodeTypeId);
    if (nodeType) {
      totalMicroEuros += timePriceToMonthlyPrice(nodeType.price) * count;
    }
  });

  // Add application costs
  project.applications.forEach(appId => {
    const app = applications.find(a => a.id === appId);
    if (app) {
      totalMicroEuros += timePriceToMonthlyPrice(app.price);
    }
  });

  return totalMicroEuros;
};
